import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'

interface Asset {
  id: string
  name: string
  type: string
  status: 'ACTIVE' | 'MAINTENANCE' | 'RETIRED' | 'DAMAGED'
  description?: string
  location?: string
  requiresSpecializedOperator?: boolean
  createdAt: string
  updatedAt: string
  images?: string[]

  // Vehicle Asset fields (directly in database)
  licensePlate?: string
  vin?: string
  make?: string
  model?: string
  year?: number
  color?: string

  // Legacy specifications (for backward compatibility)
  specifications?: {
    make?: string
    model?: string
    year?: number
    capacity?: number
    equipment?: string[]
    customFields?: Record<string, any>
  }
  purchaseDate?: string
  lastMaintenanceDate?: string
  nextMaintenanceDate?: string
  compatibleServices?: string[]
  dailyCost?: number

  // Burial plot specific fields - stored directly in database
  plotNumber?: string
  section?: string
  row?: string
  plotSize?: 'ADULT' | 'CHILD'
  dimensions?: {
    length?: number
    width?: number
    depth?: number
  }
  capacity?: number
  isAvailable?: boolean
  totalCapacity?: number
  usedCapacity?: number
  lastBurialDate?: string
}

const ProviderAssetsPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null)
  const [formData, setFormData] = useState<Partial<Asset>>({})
  const [saving, setSaving] = useState(false)

  const assetTypes = [
    'AMBULANCE_VEHICLE',
    'BURIAL_PLOT',
  ]

  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800',
    MAINTENANCE: 'bg-yellow-100 text-yellow-800',
    RETIRED: 'bg-gray-100 text-gray-800',
    DAMAGED: 'bg-red-100 text-red-800'
  }

  const statusIcons = {
    ACTIVE: CheckCircleIcon,
    MAINTENANCE: ClockIcon,
    RETIRED: XCircleIcon,
    DAMAGED: XCircleIcon
  }

  useEffect(() => {
    fetchAssets()
  }, [])

  const fetchAssets = async () => {
    try {
      // Use the new separated assets endpoint
      const response = await api.get('/provider-dashboard/assets/separated')
      setAssets(response.data)
    } catch (err: any) {
      console.log('Error fetching assets:', err.message || err)
      setAssets([]) // Set empty array to show "No assets yet" message
    } finally {
      setLoading(false)
    }
  }

  const handleAddAsset = () => {
    setEditingAsset(null)
    setFormData({
      name: '',
      type: 'AMBULANCE_VEHICLE',
      status: 'ACTIVE',
      description: '',
      specifications: {},
      requiresSpecializedOperator: false
    })
    setShowModal(true)
  }

  const handleEditAsset = (asset: Asset) => {
    navigate(`/provider-dashboard/assets/${asset.id}`)
  }

  const handleSaveAsset = async () => {
    setSaving(true)
    try {
      let response

      if (editingAsset) {
        // Determine the correct endpoint based on asset type
        const isVehicle = ['AMBULANCE_VEHICLE', 'HEARSE', 'TRANSPORT_VEHICLE'].includes(formData.type)
        const endpoint = isVehicle
          ? `/provider-dashboard/assets/vehicles/${editingAsset.id}`
          : `/provider-dashboard/assets/${editingAsset.id}`

        response = await api.put(endpoint, formData)
        setAssets(assets.map(a => a.id === response.data.id ? response.data : a))
        toast.success('Asset updated successfully')
      } else {
        // Use the new unified endpoint
        response = await api.post('/provider-dashboard/assets/by-type', formData)
        setAssets([...assets, response.data])
        toast.success('Asset added successfully')
      }

      setShowModal(false)
      setEditingAsset(null)
      setFormData({})
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save asset')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAsset = async (assetId: string, assetType?: string) => {
    if (!confirm('Are you sure you want to delete this asset?')) return

    try {
      // Determine the correct endpoint based on asset type
      const isVehicle = assetType && ['AMBULANCE_VEHICLE', 'HEARSE', 'TRANSPORT_VEHICLE'].includes(assetType)
      const endpoint = isVehicle
        ? `/provider-dashboard/assets/vehicles/${assetId}`
        : `/provider-dashboard/assets/${assetId}`

      await api.delete(endpoint)
      setAssets(assets.filter(a => a.id !== assetId))
      toast.success('Asset deleted successfully')
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to delete asset')
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSpecificationChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [field]: value
      }
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-primary-100 p-3 rounded-full">
              <TruckIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Asset Management</h1>
              <p className="text-gray-600">Manage your vehicles, equipment, and resources</p>
            </div>
          </div>
          <button
            onClick={handleAddAsset}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Asset</span>
          </button>
        </div>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assets.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No assets</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first asset.</p>
            <div className="mt-6">
              <button
                onClick={handleAddAsset}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Asset
              </button>
            </div>
          </div>
        ) : (
          assets.map((asset) => {
            const StatusIcon = statusIcons[asset.status as keyof typeof statusIcons]
            const isVehicle = ['AMBULANCE_VEHICLE', 'HEARSE', 'TRANSPORT_VEHICLE'].includes(asset.type)
            const isBurialPlot = asset.type === 'BURIAL_PLOT'

            return (
              <div key={asset.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${
                      isVehicle ? 'bg-blue-100' : isBurialPlot ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      {isVehicle ? (
                        <TruckIcon className="h-6 w-6 text-blue-600" />
                      ) : isBurialPlot ? (
                        <BuildingOfficeIcon className="h-6 w-6 text-green-600" />
                      ) : (
                        <WrenchScrewdriverIcon className="h-6 w-6 text-gray-600" />
                      )}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type.replace(/_/g, ' ')}</p>
                      {isBurialPlot && asset.plotNumber && (
                        <p className="text-xs text-gray-400">Plot #{asset.plotNumber}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[asset.status as keyof typeof statusColors]}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {asset.status}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {asset.licensePlate && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">License:</span>
                      <span className="ml-2">{asset.licensePlate}</span>
                    </div>
                  )}
                  {/* Display vehicle info from direct fields first, then fallback to specifications */}
                  {(asset.make && asset.model) ? (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Model:</span>
                      <span className="ml-2">
                        {asset.make} {asset.model}
                      </span>
                    </div>
                  ) : asset.specifications?.make && asset.specifications?.model ? (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Model:</span>
                      <span className="ml-2">
                        {asset.specifications.make} {asset.specifications.model}
                      </span>
                    </div>
                  ) : null}
                  {asset.year && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Year:</span>
                      <span className="ml-2">{asset.year}</span>
                    </div>
                  )}
                  {asset.color && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Color:</span>
                      <span className="ml-2">{asset.color}</span>
                    </div>
                  )}
                  {asset.vin && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">VIN:</span>
                      <span className="ml-2">{asset.vin}</span>
                    </div>
                  )}
                  {asset.dailyCost && (
                    <div className="flex items-center text-sm text-gray-600">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Daily Cost:</span>
                      <span className="ml-2">RM{asset.dailyCost}</span>
                    </div>
                  )}
                  {asset.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Location:</span>
                      <span className="ml-2">{asset.location}</span>
                    </div>
                  )}
                </div>

                {/* Additional Burial Plot Details */}
                {asset.type === 'BURIAL_PLOT' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    {asset.plotNumber && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Plot Number:</span>
                        <span className="ml-2 font-medium text-gray-900">{asset.plotNumber}</span>
                      </div>
                    )}
                    {asset.section && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Section:</span>
                        <span className="ml-2">{asset.section}</span>
                      </div>
                    )}
                    {asset.row && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Row:</span>
                        <span className="ml-2">{asset.row}</span>
                      </div>
                    )}
                    {asset.plotSize && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Plot Size:</span>
                        <span className="ml-2 capitalize">{asset.plotSize.toLowerCase()}</span>
                      </div>
                    )}
                    {asset.dimensions && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Dimensions:</span>
                        <span className="ml-2">
                          {asset.dimensions.length && asset.dimensions.width && asset.dimensions.depth
                            ? `${asset.dimensions.length}m × ${asset.dimensions.width}m × ${asset.dimensions.depth}m`
                            : asset.dimensions.length && asset.dimensions.width
                            ? `${asset.dimensions.length}m × ${asset.dimensions.width}m`
                            : 'N/A'}
                        </span>
                      </div>
                    )}
                    {asset.isAvailable !== undefined && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium">Status:</span>
                        <span className={`ml-2 font-medium ${
                          asset.isAvailable ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {asset.isAvailable ? 'Available' : 'Occupied'}
                        </span>
                      </div>
                    )}
                                      </div>
                )}

                {asset.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">{asset.description}</p>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
                  <button
                    onClick={() => navigate(`/provider-dashboard/assets/${asset.id}`)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    Manage
                  </button>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditAsset(asset)}
                      className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAsset(asset.id, asset.type)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Asset Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[85vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Type *
                    </label>
                    <select
                      value={formData.type || ''}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      {assetTypes.map(type => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status *
                    </label>
                    <select
                      value={formData.status || ''}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      {Object.keys(statusColors).map(status => (
                        <option key={status} value={status}>
                          {status.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.type !== 'BURIAL_PLOT' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Daily Cost (RM)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.dailyCost || ''}
                        onChange={(e) => handleInputChange('dailyCost', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}
                </div>

                {(formData.type === 'AMBULANCE_VEHICLE' ||
                  formData.type === 'HEARSE' ||
                  formData.type === 'TRANSPORT_VEHICLE') && (
                  <>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 border-t border-gray-200 pt-4">
                      Vehicle Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          License Plate
                        </label>
                        <input
                          type="text"
                          value={formData.licensePlate || ''}
                          onChange={(e) => handleInputChange('licensePlate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., ABC 1234"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          VIN
                        </label>
                        <input
                          type="text"
                          value={formData.vin || ''}
                          onChange={(e) => handleInputChange('vin', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Vehicle identification number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Make
                        </label>
                        <input
                          type="text"
                          value={formData.make || ''}
                          onChange={(e) => handleInputChange('make', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., Toyota"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Model
                        </label>
                        <input
                          type="text"
                          value={formData.model || ''}
                          onChange={(e) => handleInputChange('model', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., HiAce Ambulance"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year
                        </label>
                        <input
                          type="number"
                          value={formData.year || ''}
                          onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., 2022"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color
                        </label>
                        <input
                          type="text"
                          value={formData.color || ''}
                          onChange={(e) => handleInputChange('color', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., White"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={formData.location || ''}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., Kuala Lumpur, Malaysia"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Burial Plot Specific Fields */}
                {formData.type === 'BURIAL_PLOT' && (
                  <>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Burial Plot Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Plot Number
                        </label>
                        <input
                          type="text"
                          value={formData.plotNumber || ''}
                          onChange={(e) => handleInputChange('plotNumber', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., A-123"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Availability Status
                        </label>
                        <select
                          value={formData.isAvailable !== undefined ? String(formData.isAvailable) : ''}
                          onChange={(e) => handleInputChange('isAvailable', e.target.value === 'true')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select status</option>
                          <option value="true">Available</option>
                          <option value="false">Occupied</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Section
                        </label>
                        <input
                          type="text"
                          value={formData.section || ''}
                          onChange={(e) => handleInputChange('section', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., Section A"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Row
                        </label>
                        <input
                          type="text"
                          value={formData.row || ''}
                          onChange={(e) => handleInputChange('row', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g., Row 5"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Plot Capacity
                        </label>
                        <input
                          type="number"
                          value={formData.capacity || ''}
                          onChange={(e) => handleInputChange('capacity', parseInt(e.target.value) || undefined)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Number of people that can be buried"
                        />
                      </div>
                    </div>

                    {/* Plot Size and Dimensions Grouped Together */}
                    <div className="border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Plot Size & Dimensions</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Plot Size
                          </label>
                          <select
                            value={formData.plotSize || ''}
                            onChange={(e) => {
                              const plotSize = e.target.value as 'ADULT' | 'CHILD'
                              const defaultDimensions = plotSize === 'ADULT'
                                ? { length: 2.5, width: 1.2, depth: 1.8 }
                                : { length: 1.8, width: 0.9, depth: 1.2 }

                              handleInputChange('plotSize', plotSize)
                              handleInputChange('dimensions', defaultDimensions)
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select size</option>
                            <option value="ADULT">Adult</option>
                            <option value="CHILD">Child</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Dimensions will auto-populate based on plot size
                          </p>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Length (m)
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={formData.dimensions?.length || ''}
                              onChange={(e) => handleInputChange('dimensions', {
                                ...formData.dimensions,
                                length: parseFloat(e.target.value) || undefined
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                              placeholder="2.5"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Width (m)
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={formData.dimensions?.width || ''}
                              onChange={(e) => handleInputChange('dimensions', {
                                ...formData.dimensions,
                                width: parseFloat(e.target.value) || undefined
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                              placeholder="1.2"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Depth (m)
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              value={formData.dimensions?.depth || ''}
                              onChange={(e) => handleInputChange('dimensions', {
                                ...formData.dimensions,
                                depth: parseFloat(e.target.value) || undefined
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                              placeholder="1.8"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                                      </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="requiresSpecializedOperator"
                    checked={formData.requiresSpecializedOperator || false}
                    onChange={(e) => handleInputChange('requiresSpecializedOperator', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="requiresSpecializedOperator" className="ml-2 block text-sm text-gray-900">
                    Requires specialized operator
                  </label>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAsset}
                  disabled={saving}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    editingAsset ? 'Update' : 'Add'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProviderAssetsPage